import { ChangeEvent, FC, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';

import { BUSINESS_PLAN_TYPES, IBusinessToolType } from 'constants/businessPlan';
import { useBreakpoints } from 'hooks';
import { useUpdateBusinessPlan } from 'services/businessPlan/hooks';
import { useBusinessToolStore } from 'store/useBusinessToolStore';
import { getBusinessPlan, getBusinessPlanType, getSetBusinessPlanType } from 'store/useBusinessToolStore/selectors';

import { useAuthenticated } from 'hooks/useAuthenticated';
import usePromiseStatus from 'hooks/usePromiseStatus';

import { ONE_PAGE_OPTIONS_DEU } from 'components/BusinessToolForm/constants/deu/options_deu';
import { ONE_PAGE_OPTIONS_ES } from 'components/BusinessToolForm/constants/es/options_es';
import { ONE_PAGE_OPTIONS } from 'components/BusinessToolForm/constants/options';
import { ONE_PAGE_OPTIONS_UA } from 'components/BusinessToolForm/constants/ua/options_ua';

import { getValidatedMetadata } from 'helpers/metadataParser';
import ToastService from 'helpers/ToastService';

import { DesktopView } from './DesktopView';
import { MobileView } from './MobileView';

export const OnePageTypeSwitch: FC = () => {
  const { t, i18n } = useTranslation();
  const [onePageType, setOnePageType] = useState<IBusinessToolType>(BUSINESS_PLAN_TYPES.ONE_PAGE_SHORT);
  const { isMobile } = useBreakpoints();

  const { id = '' } = useParams();
  const { isAuthenticated, isAuthConfiguring } = useAuthenticated();
  const businessPlanType = useBusinessToolStore(getBusinessPlanType);
  const businessPlan = useBusinessToolStore(getBusinessPlan);
  const setBusinessPlanType = useBusinessToolStore(getSetBusinessPlanType);

  const getChosenOnePageOptions = (lang: string) => {
    switch (lang) {
      case 'de':
        return ONE_PAGE_OPTIONS_DEU;

      case 'uk':
        return ONE_PAGE_OPTIONS_UA;

      case 'es':
        return ONE_PAGE_OPTIONS_ES;

      default:
        return ONE_PAGE_OPTIONS;
    }
  };

  const updateMutation = useUpdateBusinessPlan();

  const { track, isPending } = usePromiseStatus();

  useEffect(() => {
    setOnePageType(businessPlanType);
  }, [businessPlanType]);

  const radioChangeHandler = async ({ target: { value } }: ChangeEvent<HTMLInputElement>) => {
    const template = value as IBusinessToolType;

    if (template === onePageType) return;

    try {
      if (isAuthenticated || isAuthConfiguring) {
        const params = {
          id,
          body: { type: template, metadata: JSON.stringify({ ...getValidatedMetadata(businessPlan.metadata) }) },
        };
        await track(updateMutation.mutateAsync(params));
      }
      setBusinessPlanType(template);
    } catch (error) {
      return ToastService.showError(t('couldNotUpdateTemplate'));
    }

    setOnePageType(template);
  };

  const ViewComponent = isMobile ? MobileView : DesktopView;

  return (
    <ViewComponent
      isPending={isPending}
      value={onePageType}
      onChange={radioChangeHandler}
      options={getChosenOnePageOptions(i18n.language)}
    />
  );
};
